import { Component, ErrorHandler, Injector, OnInit, ViewChild, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'

import { MatSnackBar } from '@angular/material/snack-bar'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { AsiakasService, AsiakkaanAvainTiedot } from '../../../_angular/service/asiakas/asiakas.service'

import { Observable, Subject, firstValueFrom } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { MatDialog } from '@angular/material/dialog'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { DateService } from '@lemon/core/service/date.service'
import {
  HolviAnnaAsiakkaanTilitapahtumatRequest,
  HolviAnnaAsiakkaanTilitapahtumatResponse,
  HolviCustomerDataCompany,
  HolviCustomerDataUser,
  HolviGetAttachmentRequest,
  HolviGetAttachmentResponse,
  HolviTransaction,
} from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/holvi-backend'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { HolviTransactionDetailsDialog } from './holvi-api-transaction-details.dialog'

import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'

interface HolviTili {
  iban: string
  handle: string
}

interface HolviTapahtumienHakuLomake {
  valittuTili: HolviTili | null
  alkaa: Date | null
  loppuu: Date | null
}

interface HolviTransactionListauksessa {
  /** Same as payment_audit_identifier */
  id: string
  /** time as date */
  date: Date
  amount: number
  /** payer_payee */
  maksajaTaiSaaja: string
  liitteet: HolviLiite[]
}

interface HolviLiite {
  nimi: string
  avain: string
}
@Component({
  selector: '[app-holvi-transactions]',
  templateUrl: './holvi-api-query.component.html',
  styleUrls: ['./holvi-api-query.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HolviApiQueryComponent implements OnInit {
  @ViewChild(MatSort, { static: true }) _sort: MatSort

  asiakasAvainObservable: Observable<AsiakkaanAvainTiedot>
  readonly holviCustomerData = signal<HolviCustomerDataCompany>(undefined)
  readonly holviUserData = signal<HolviCustomerDataUser>(undefined)
  lomake: FieldTree<HolviTapahtumienHakuLomake>
  readonly commonError = signal<string>(undefined)
  readonly tilit = signal<HolviTili[]>(undefined)

  private _cachedTransactionsListauksessa: { [poolHandle: string]: HolviTransactionListauksessa[] } = {}
  private _transactionDetailsMap: Map<string, HolviTransaction> = new Map()

  dataSource = new MatTableDataSource<HolviTransactionListauksessa>([])
  naytettavatKolumnit: string[] = ['id', 'date', 'amount', 'maksajaTaiSaaja', 'liitteet']

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    private _errorHandler: ErrorHandler,
    private _asiakasService: AsiakasService,
    private _firebase: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _dateService: DateService,
    private _ladataanService: LadataanService,
    private _matDialog: MatDialog,
    private _formValidationService: FormValidationService,
    private _fileSaverService: FileSaverService,
  ) {}

  ngOnInit() {
    this.asiakasAvainObservable = this._asiakasService.nykyinenAsiakasAvainObservable

    this.lomake = form(
      signal<HolviTapahtumienHakuLomake>({ valittuTili: null, alkaa: this._dateService.kuukaudenEnsimmainen(new Date()), loppuu: new Date() }),
      polku => {
        required(polku.alkaa)
        required(polku.loppuu)
      },
      { injector: this._injector },
    )

    // this.asiakasAvainObservable.pipe(
    //   takeUntil(this._ngUnsubscribe)
    // ).subscribe(asiakas => {
    //   if (asiakas?.avain) {
    //     this._ladataanService.aloitaLataaminen()
    //     return this.fetchTransactions(asiakas.avain)
    //       .finally(() => this._ladataanService.lopetaLataaminen())
    //   }
    // })

    arvonMuutokset(this.lomake.valittuTili, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(valittuTili => {
        if (!valittuTili) {
          const allTransactions: HolviTransactionListauksessa[] = []
          for (const tili of this.tilit()) {
            for (const transaction of this._cachedTransactionsListauksessa[tili.handle]) {
              allTransactions.push(transaction)
            }
          }
          this.holviTransactionDataSource.data = allTransactions || []
          return
        }
        this.holviTransactionDataSource.data = this._cachedTransactionsListauksessa[valittuTili.handle]
      })

    // this.lomake.alkaa().value()Changes.pipe(
    //   withLatestFrom(this.asiakasAvainObservable),
    //   takeUntil(this._ngUnsubscribe)
    // ).subscribe(([alkaa, asiakas]) => {
    //   if (alkaa) {
    //     this._ladataanService.aloitaLataaminen()
    //     return this.fetchTransactions(asiakas.avain)
    //       .finally(() => this._ladataanService.lopetaLataaminen())
    //   }
    // })

    // this.lomake.loppuu().value()Changes.pipe(
    //   withLatestFrom(this.asiakasAvainObservable),
    //   takeUntil(this._ngUnsubscribe)
    // ).subscribe(([loppuu, asiakas]) => {
    //   if (loppuu) {
    //     this._ladataanService.aloitaLataaminen()
    //     return this.fetchTransactions(asiakas.avain)
    //       .finally(() => this._ladataanService.lopetaLataaminen())
    //   }
    // })

    this._sort.sort({
      disableClear: false,
      id: 'date',
      start: 'asc',
    })
    this.dataSource.sort = this._sort
  }

  async hae() {
    const asiakas = await firstValueFrom(this.asiakasAvainObservable)
    const alkaa = this._dateService.dateToNumber(this.lomake.alkaa().value())
    const loppuu = this._dateService.dateToNumber(this.lomake.loppuu().value())
    if (!alkaa || !loppuu || !asiakas?.avain) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }
    this._ladataanService.aloitaLataaminen()
    await this.fetchTransactions(asiakas.avain).finally(() => this._ladataanService.lopetaLataaminen())
  }

  async fetchTransactions(asiakasAvain: string) {
    const req: HolviAnnaAsiakkaanTilitapahtumatRequest = {
      asiakasAvain: asiakasAvain,
      start: this._dateService.dateToNumber(this.lomake.alkaa().value()),
      end: this._dateService.dateToNumber(this.lomake.loppuu().value()),
    }

    return this._firebase.functionsCall<HolviAnnaAsiakkaanTilitapahtumatRequest, HolviAnnaAsiakkaanTilitapahtumatResponse>('holviFetchCustomerTransactions', req).then(resp => {
      if (!resp || resp.e) {
        if (resp.e === 'no-tokens') {
          this.commonError.set('Holvi-yhteys puuttuu.')
        } else if (resp.e === 'no-pools') {
          this.commonError.set('Asiakkaalla ei ole tilejä.')
        } else {
          this._errorHandler.handleError(new Error('Holvin tilitapahtumien haku epäonnistui! ' + resp?.e || 'no-response'))
          this.commonError.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
        }
      } else {
        this.holviCustomerData.set(resp.company)
        this.holviUserData.set(resp.user)

        const poolToIbanMap: Map<string, string> = new Map()
        for (const paymentAccount of resp.company?.payment_accounts ?? []) {
          if (paymentAccount.pool_handle && paymentAccount.iban) {
            poolToIbanMap.set(paymentAccount.pool_handle, paymentAccount.iban)
          }
        }

        const tilit = Object.keys(resp.transactions || {}).map(handle => {
          const tili: HolviTili = {
            handle: handle,
            iban: poolToIbanMap.get(handle) ?? handle,
          }
          return tili
        })
        this.tilit.set(tilit)

        if (this.lomake.valittuTili().value() && tilit.find(tili => tili.handle === this.lomake.valittuTili().value().handle)) {
          // IBAN filter is active
          this.holviTransactionDataSource.data = resp.transactions[this.lomake.valittuTili().value().handle].map(tr => this._convertToListausData(tr))
          return
        }

        if (tilit.length) {
          const allTransactions: HolviTransactionListauksessa[] = []
          for (const tili of tilit) {
            for (const transaction of resp.transactions[tili.handle]) {
              const listauksessa = this._convertToListausData(transaction)
              allTransactions.push(listauksessa)

              if (!this._cachedTransactionsListauksessa[tili.handle]) {
                this._cachedTransactionsListauksessa[tili.handle] = []
              }
              this._cachedTransactionsListauksessa[tili.handle].push(listauksessa)

              this._transactionDetailsMap.set(transaction.payment_audit_identifier, transaction)
            }
          }
          this.holviTransactionDataSource.data = allTransactions || []
        }
      }
    })
  }

  get holviTransactionDataSource(): MatTableDataSource<HolviTransactionListauksessa> {
    return this.dataSource
  }

  naytaDetaileja(listauksessa: HolviTransactionListauksessa) {
    const detailit = this._transactionDetailsMap.get(listauksessa.id)
    this._matDialog.open(HolviTransactionDetailsDialog, { data: detailit })
  }

  async downloadAttachment(liite: HolviLiite, event: MouseEvent) {
    event?.preventDefault()
    event?.stopPropagation()

    const asiakasAvaimet = await firstValueFrom(this.asiakasAvainObservable)

    const req: HolviGetAttachmentRequest = {
      asiakasAvain: asiakasAvaimet.avain,
      attachmentId: liite.avain,
    }

    this._ladataanService.aloitaLataaminen()

    return this._firebase
      .functionsCall<HolviGetAttachmentRequest, HolviGetAttachmentResponse>('holviGetTransactionAttachment', req)
      .then(resp => {
        if (!resp || resp.e || !resp.base64File?.length) {
          if (resp.e === 'no-tokens') {
            this.commonError.set('Holvi-yhteys puuttuu.')
          } else {
            throw new Error('Holvin tilitapahtuman liitteen lataus epäonnistui! ' + (resp?.e || 'no-response'))
          }
        } else {
          if (resp.base64File) {
            // Presume that nameless attachments are PDFs (created by Holvi's system)
            const fileName = liite.nimi || liite.avain + '.pdf'
            this._fileSaverService.saveBase64AsGuessedType(resp.base64File, fileName)
          }
        }
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        this._snackbar.open('Liitteen lataus epäonnistui! Ole hyvä ja yritä uudelleen.', 'OK', { duration: 5000, verticalPosition: 'bottom' })
      })
      .finally(() => this._ladataanService.lopetaLataaminen())
  }

  private _convertToListausData(holviTransaction: HolviTransaction): HolviTransactionListauksessa {
    const liitteet: HolviLiite[] = []

    if (holviTransaction.debts?.length > 0) {
      for (const debt of holviTransaction.debts) {
        for (const attachment of debt.attachments) {
          liitteet.push({
            nimi: attachment.original_filename,
            avain: attachment.code,
          })
        }
      }
    }

    return {
      id: holviTransaction.payment_audit_identifier,
      date: this._dateService.parsiPaivaIsoFormaatista(holviTransaction.time),
      amount: +holviTransaction.amount,
      maksajaTaiSaaja: holviTransaction.payer_payee,
      liitteet: liitteet,
    }
  }
}
